import { createRequest } from '../helpers/api_utils';
import { CV_CREDITS_EXHAUSTED, FeatureCredits } from '../types/credits_types';
import { apiUrl } from './api_url';

export interface CandiBankUploadResponse {
  message?: string;
  uploadedCount?: number;
  uploaded?: Array<{ id: string; baseFilename: string; status: string; charged?: boolean }>;
  /** Files refused for want of credits. Non-empty with a 200 only on a multi-file request. */
  blocked?: string[];
  credits?: FeatureCredits;
  success?: boolean;
  error?: string;
  data?: any;
}

/** The month's CV allowance is spent. Carries the credits block so the UI can show the counter. */
export class NoCreditsError extends Error {
  readonly kind = 'no_credits' as const;
  readonly credits?: FeatureCredits;

  constructor(credits?: FeatureCredits) {
    super('Monthly CV credit limit reached');
    this.name = 'NoCreditsError';
    this.credits = credits;
  }
}

export function isNoCreditsError(err: unknown): err is NoCreditsError {
  return !!err && typeof err === 'object' && (err as { kind?: string }).kind === 'no_credits';
}

/** Headline counts for the bank page plus this month's credits. */
export function getCandiBankStats() {
  return fetch(createRequest(`${apiUrl}/candi-bank/stats`, 'GET', 'application/json', true));
}

/**
 * Uploads a single CV file to /candi-bank/upload endpoint using multipart/form-data.
 * Handles duplicate detection (E11000 / NotUniqueError) and the 402 credit refusal.
 */
export async function uploadCandiBankSingleFile(
  file: File,
  onProgress?: (progressPercent: number) => void
): Promise<CandiBankUploadResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = `${apiUrl}/candi-bank/upload`;

    const formData = new FormData();
    formData.append('files', file);

    xhr.open('POST', url, true);

    const token = localStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete);
        }
      };
    }

    xhr.onload = () => {
      let res: any = {};
      const responseRaw = xhr.responseText || '';
      try {
        res = responseRaw ? JSON.parse(responseRaw) : {};
      } catch {
        res = { data: responseRaw };
      }

      // Out of credits: a stable code, never a message match.
      if (xhr.status === 402 || res.error === CV_CREDITS_EXHAUSTED) {
        reject(new NoCreditsError(res.credits));
        return;
      }

      // Check for duplicate key / NotUniqueError in response
      const isDuplicateError =
        responseRaw.includes('E11000') ||
        responseRaw.includes('NotUniqueError') ||
        responseRaw.includes('duplicate key error') ||
        responseRaw.includes('file_hash');

      if (isDuplicateError) {
        reject(new Error('Duplicate CV: This file already exists in Candi Bank'));
        return;
      }

      if (xhr.status >= 200 && xhr.status < 300 && res.success !== false && !res.error) {
        resolve(res);
      } else {
        const errMsg = res.error || res.message || res.detail || `Server Error (${xhr.status})`;
        reject(new Error(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg)));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network error occurred during CV upload.'));
    };

    xhr.onabort = () => {
      reject(new Error('Upload was aborted.'));
    };

    xhr.send(formData);
  });
}
